import type { QueryClient, QueryKey } from "@tanstack/react-query";

type Entry = { value: unknown; expiresAt: number };
const store = new Map<string, Entry>();
const inflight = new Map<string, Promise<unknown>>();

type EnsureArg = Parameters<QueryClient["ensureQueryData"]>[0];

function refresh<T>(
  queryClient: QueryClient,
  opts: { queryKey: QueryKey } & object,
  key: string,
  ttlMs: number,
): Promise<T> {
  const pending = inflight.get(key) as Promise<T> | undefined;
  if (pending) return pending;
  const promise = (queryClient.ensureQueryData(opts as EnsureArg) as Promise<T>)
    .then((value) => {
      store.set(key, { value, expiresAt: Date.now() + ttlMs });
      return value;
    })
    .finally(() => {
      inflight.delete(key);
    });
  inflight.set(key, promise);
  return promise;
}

export async function cachedEnsureQueryData<T>(
  queryClient: QueryClient,
  opts: { queryKey: QueryKey } & object,
  ttlMs: number,
): Promise<T> {
  if (typeof window !== "undefined") {
    return queryClient.ensureQueryData(opts as EnsureArg) as Promise<T>;
  }
  const key = JSON.stringify(opts.queryKey);
  const hit = store.get(key);
  if (hit) {
    if (hit.expiresAt <= Date.now()) {
      refresh<T>(queryClient, opts, key, ttlMs).catch(() => {});
    }
    queryClient.setQueryData(opts.queryKey, hit.value as T);
    return hit.value as T;
  }
  const value = await refresh<T>(queryClient, opts, key, ttlMs);
  queryClient.setQueryData(opts.queryKey, value);
  return value;
}
